/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { Dispatch, SetStateAction, useLayoutEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";
import { useResizeObserver } from "@usefy/use-resize-observer";

import { CloseIcon, colors } from "@dotnet/telematics-design-system";

import { ROUTES, useMediaQueries } from "../../../../../shared";
import RectangleLoader from "../../../../../shared/components/loaders/RectangleLoader";
import { useOverflow } from "../../../../../shared/hooks/useOverflow";
import Button from "../../../../Button/Button";
import CopyButton from "../../../../CopyButton/CopyButton";
import TextLink from "../../../../TextLink/TextLink";
import { TEXT_LINK_SIZES } from "../../../../TextLink/types";
import { LongTextTooltip } from "../../../../Tooltip/LongTextTooltip";
import Tooltip from "../../../../Tooltip/Tooltip";
import {
  MARGIN_LEFT_AND_RIGHT,
  MAX_DESCRIPTION_LENGTH
} from "../../../constants";
import { renderTitle } from "../../../functions";
import { useStyles } from "../../../styles";
import { DetailsDto } from "../../../types";

interface IDetailsTitleAreaProps {
  displayLoader: boolean;
  detailsData: DetailsDto;
  onClose: (() => void) | undefined;
  route: ROUTES;
  setTitleAreaHeight: Dispatch<SetStateAction<number | null>>;
}

const DetailsTitleArea = ({
  displayLoader,
  detailsData,
  onClose,
  route,
  setTitleAreaHeight
}: IDetailsTitleAreaProps) => {
  const { fromMd } = useMediaQueries();
  const { t } = useTranslation();
  const { measuredRef, isOverflowing } = useOverflow();

  const { ref, height = 1 } = useResizeObserver();

  const [showMoreText, setShowMoreText] = useState<boolean>(false);

  const {
    detailsTitle,
    detailsTitleArea,
    detailsTitleAreaWithLoader,
    closeDetailsButton,
    copyButtonCustomStyle,
    detailsTitleWithInlineAction
  } = useStyles();

  useLayoutEffect(() => {
    setTitleAreaHeight(height + 46); // 46 is total number of padding top and bottom
  }, [showMoreText, height, setTitleAreaHeight, isOverflowing]);

  const showCloseButton = (fromMd && detailsData) || (fromMd && displayLoader);
  const hasCopyButton = renderTitle(detailsData, route)?.copy;
  const titleText = t(`Details##${renderTitle(detailsData, route)?.title}`);
  const titleValueFromFetchedData = renderTitle(detailsData, route)?.value;
  const marginAfterDescriptionSpan = 8;

  const handleToggleShowMore = () => {
    setShowMoreText(!showMoreText);
  };

  const titleSpan = (textLine: string, margin: number) => (
    <span
      css={css({ color: colors.textPlaceholder, marginRight: `${margin}px` })}
    >
      {textLine}:
    </span>
  );

  const showMore = (textToShow: any) => {
    const isEmpty = !textToShow || textToShow === "";
    const marginAfterTitleSpan = 4;

    if (textToShow?.length > MAX_DESCRIPTION_LENGTH) {
      return (
        <Typography variant="body2Medium" sx={{ overflowWrap: "break-word" }}>
          {titleSpan(t("General##Description"), marginAfterTitleSpan)}
          {showMoreText
            ? textToShow
            : `${textToShow.slice(0, MAX_DESCRIPTION_LENGTH)}...`}{" "}
          <span>
            <TextLink
              size={TEXT_LINK_SIZES.Normal}
              onClick={handleToggleShowMore}
              sx={{ overflowWrap: "break-word" }}
            >
              {showMoreText ? t("Details##show less") : t("Details##show more")}
            </TextLink>
          </span>
        </Typography>
      );
    }

    return (
      <Typography variant="body2Medium">
        {titleSpan(t("General##Description"), marginAfterTitleSpan)}
        {isEmpty ? "-" : textToShow}
      </Typography>
    );
  };

  return (
    <div
      ref={ref}
      data-testid="details-title-area"
      css={css([
        {
          width: `calc(100% - ${MARGIN_LEFT_AND_RIGHT})`,
          height: displayLoader ? "102px" : "auto"
        },
        detailsTitleArea
      ])}
    >
      <div css={css(detailsTitleAreaWithLoader)}>
        {displayLoader ? (
          <RectangleLoader
            height="24px"
            width="216px"
            testId="details-data-number-loader"
          />
        ) : (
          detailsData && (
            <LongTextTooltip
              title={titleValueFromFetchedData}
              open={isOverflowing}
            >
              <Typography
                ref={measuredRef}
                data-testid="details-title"
                variant="h6"
                css={css([
                  detailsTitle,
                  hasCopyButton && detailsTitleWithInlineAction
                ])}
              >
                {renderTitle(detailsData, route)?.title &&
                  titleSpan(titleText, marginAfterDescriptionSpan)}
                {titleValueFromFetchedData ?? "-"}

                {hasCopyButton && titleValueFromFetchedData && (
                  <span>
                    <CopyButton
                      testId="title-copy-button"
                      size="normal"
                      copiedValue={titleValueFromFetchedData}
                      buttonCustomStyle={copyButtonCustomStyle as {}}
                    />
                  </span>
                )}
              </Typography>
            </LongTextTooltip>
          )
        )}

        {showCloseButton && (
          <Tooltip
            data-testid="close-details-tooltip"
            title={t("Details##close details")}
            placement="top"
            size="medium"
            hasArrow
          >
            <Button
              variant="iconOnly"
              size="small"
              color="white"
              icon={<CloseIcon />}
              onClick={onClose}
              css={css(closeDetailsButton)}
            />
          </Tooltip>
        )}
      </div>

      <div>
        {displayLoader ? (
          <RectangleLoader
            height="20"
            width={fromMd ? "100%" : "380px"}
            customStyle={{ marginTop: fromMd ? "unset" : "8px" }}
          />
        ) : (
          detailsData?.comment && showMore(detailsData.comment)
        )}
      </div>
    </div>
  );
};

export default DetailsTitleArea;
